import { z } from "zod";
import { getAppBaseUrl } from "../app-url";

export { APP_SIGNUP_URL } from "../app-url";

export const ATTRIBUTION_COOKIE_NAME = "rr_attribution";
export const ATTRIBUTION_COOKIE_MAX_AGE = 60 * 60 * 24 * 90; // 90 days
export const ATTRIBUTION_COOKIE_DOMAIN = ".robotrock.io";

const UTM_PARAM_MAP = {
  utm_source: "utmSource",
  utm_medium: "utmMedium",
  utm_campaign: "utmCampaign",
  utm_content: "utmContent",
  utm_term: "utmTerm",
  gclid: "gclid",
} as const;

export const attributionSchema = z.object({
  utmSource: z.string().optional(),
  utmMedium: z.string().optional(),
  utmCampaign: z.string().optional(),
  utmContent: z.string().optional(),
  utmTerm: z.string().optional(),
  gclid: z.string().optional(),
  landingUrl: z.string().optional(),
  referrer: z.string().optional(),
  capturedAt: z.number(),
});

export type Attribution = z.infer<typeof attributionSchema>;

export function hasAttributionSignal(attribution: Partial<Attribution>): boolean {
  return Boolean(
    attribution.utmSource ||
      attribution.utmMedium ||
      attribution.utmCampaign ||
      attribution.utmContent ||
      attribution.utmTerm ||
      attribution.gclid
  );
}

export function parseAttributionFromSearchParams(
  params: URLSearchParams | Record<string, string | string[] | undefined>
): Partial<Attribution> {
  const getParam = (key: string): string | undefined => {
    if (params instanceof URLSearchParams) {
      return params.get(key)?.trim() || undefined;
    }
    const value = params[key];
    if (Array.isArray(value)) {
      return value[0]?.trim() || undefined;
    }
    return value?.trim() || undefined;
  };

  const result: Partial<Attribution> = {};

  for (const [param, field] of Object.entries(UTM_PARAM_MAP)) {
    const value = getParam(param);
    if (value) {
      result[field as keyof typeof UTM_PARAM_MAP & keyof Partial<Attribution>] =
        value;
    }
  }

  return result;
}

export function buildAttributionFromRequest(input: {
  searchParams: URLSearchParams;
  landingUrl?: string;
  referrer?: string;
  capturedAt?: number;
}): Attribution | null {
  const parsed = parseAttributionFromSearchParams(input.searchParams);
  if (!hasAttributionSignal(parsed)) {
    return null;
  }

  return attributionSchema.parse({
    ...parsed,
    landingUrl: input.landingUrl,
    referrer: input.referrer || undefined,
    capturedAt: input.capturedAt ?? Date.now(),
  });
}

export function serializeAttributionCookie(attribution: Attribution): string {
  return encodeURIComponent(JSON.stringify(attribution));
}

export function parseAttributionCookie(raw: string | undefined | null): Attribution | null {
  if (!raw) return null;

  const candidates = [raw];
  try {
    const decoded = decodeURIComponent(raw);
    if (decoded !== raw) {
      candidates.push(decoded);
    }
  } catch {
    // Keep only the raw value.
  }

  for (const candidate of candidates) {
    try {
      const parsed = attributionSchema.parse(JSON.parse(candidate));
      if (hasAttributionSignal(parsed)) {
        return parsed;
      }
    } catch {
      continue;
    }
  }

  return null;
}

export function formatAttributionCampaign(
  attribution: Partial<Attribution> | null | undefined
): string | null {
  if (!attribution) return null;
  return attribution.utmCampaign ?? attribution.utmSource ?? null;
}

export function appendAttributionToUrl(
  baseUrl: string,
  attribution: Partial<Attribution> | null | undefined,
  origin = getAppBaseUrl()
): string {
  if (!attribution || !hasAttributionSignal(attribution)) {
    return baseUrl;
  }

  const url = baseUrl.startsWith("http")
    ? new URL(baseUrl)
    : new URL(baseUrl, origin);
  const paramEntries: Array<[string, string | undefined]> = [
    ["utm_source", attribution.utmSource],
    ["utm_medium", attribution.utmMedium],
    ["utm_campaign", attribution.utmCampaign],
    ["utm_content", attribution.utmContent],
    ["utm_term", attribution.utmTerm],
    ["gclid", attribution.gclid],
  ];

  for (const [param, value] of paramEntries) {
    if (value) {
      url.searchParams.set(param, value);
    }
  }

  return baseUrl.startsWith("http")
    ? url.toString()
    : `${url.pathname}${url.search}${url.hash}`;
}

export function getAttributionCookieDomain(hostname: string): string | undefined {
  if (hostname === "localhost" || hostname.endsWith(".localhost")) {
    return undefined;
  }
  if (hostname === "robotrock.io" || hostname.endsWith(".robotrock.io")) {
    return ATTRIBUTION_COOKIE_DOMAIN;
  }
  return undefined;
}
